/**
 * 架電の「窓」(その日の最初の架電〜最後の架電)から実稼働時間を見積もる(2026-09-20 発注者指示。D-93)。
 * 根拠と実測は `docs/specs/bluebean-api.md` §14。
 *
 *  - 窓は**ログイン時間とほぼ同じ**(本番 9/1〜9/9 の268人日で 差の中央値 −0.05h)
 *  - 実稼働の定義は 出勤=拘束−休憩 / 在宅=受付＋通話＋後処理 なので、窓に**係数**を掛けて近づける
 *  - 係数はその人の過去の実績(確定した実稼働 ÷ そのときの窓)の中央値。足りなければ出勤形態の既定値
 *  - 精度(実測): 誤差の中央値 +0.02h、±30分に74%、±1時間に91%。1時間あたり取得件数は ±0.2件に83%
 *  - **確定値ではない**。翌日にオペレーターレポートを取り込んだ時点で置き換える
 */
import type { DateStr, PayType, WorkStyle } from '@core/lib/types';

/** その日・その業務(会社)の最初と最後の架電 'HH:MM:SS' */
export type CallWindow = { first: string; last: string; /** その会社での **通話回数**(BlueBean API の call_status=完了 の件数。オペレーターレポートの通話回数と同じ数え方) */ talked?: number };

/** 出勤形態×報酬区分ごとの既定の係数(実稼働 ÷ 窓 の中央値。本番 9/1〜9/9 実測) */
export const STYLE_COEFFICIENT: { style: WorkStyle; pay: PayType | null; value: number }[] = [
  { style: 'shinagawa', pay: null, value: 0.99 },
  { style: 'timee', pay: null, value: 0.99 },
  { style: 'zaitaku', pay: 'hourly', value: 0.9 },
  { style: 'zaitaku', pay: 'commission', value: 0.86 },
  { style: 'kanri', pay: null, value: 0.69 },
];
/** 係数の下限・上限(1日だけの偏りで極端な値にならないように) */
const MIN_COEF = 0.5;
const MAX_COEF = 1.1;
/** その人の係数を使うのに必要な日数 */
const MIN_DAYS = 2;
/** 係数を出すときに使う日数(新しい順) */
const MAX_DAYS = 10;
/** 短すぎる日は係数の材料にしない(窓が短いと比が暴れるため) */
const MIN_WINDOW_H = 0.5;

export function styleCoefficient(style: WorkStyle, pay: PayType): number {
  const hit = STYLE_COEFFICIENT.find((c) => c.style === style && (c.pay === null || c.pay === pay));
  return hit?.value ?? 0.9;
}

const hms = (t: string): number => {
  const [h, m, s] = t.split(':').map(Number);
  return (h ?? 0) + (m ?? 0) / 60 + (s ?? 0) / 3600;
};

/** 窓の長さ(時間)。1件しか架電が無い日は 0 になる */
export function windowHours(w: CallWindow | null | undefined): number {
  if (!w?.first || !w?.last) return 0;
  return Math.max(0, Math.round((hms(w.last) - hms(w.first)) * 100) / 100);
}

/** 業務(会社)ごとの窓 → 会社ごとの時間と合計 */
export function windowHoursByCode(windows: Record<string, CallWindow>): { total: number; byCode: Record<string, number> } {
  const byCode: Record<string, number> = {};
  let total = 0;
  for (const [code, w] of Object.entries(windows)) {
    const h = windowHours(w);
    byCode[code] = h;
    total += h;
  }
  return { total: Math.round(total * 100) / 100, byCode };
}

const median = (list: number[]): number | null => {
  if (list.length === 0) return null;
  const s = [...list].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? ((s[mid - 1] ?? 0) + (s[mid] ?? 0)) / 2 : (s[mid] ?? 0);
};

/**
 * その人の係数。過去の「確定した実稼働 ÷ そのときの窓」の中央値(新しい順に最大10日)。
 * 2日に満たなければ出勤形態の既定値を返す
 */
export function coefficientOf(history: { date: DateStr; worked: number; window: number }[], fallback: number): { value: number; days: number } {
  const usable = [...history]
    .filter((h) => h.worked > 0 && h.window >= MIN_WINDOW_H)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, MAX_DAYS);
  if (usable.length < MIN_DAYS) return { value: fallback, days: usable.length };
  const m = median(usable.map((h) => h.worked / h.window)) ?? fallback;
  return { value: Math.min(MAX_COEF, Math.max(MIN_COEF, Math.round(m * 100) / 100)), days: usable.length };
}

/** 窓 × 係数 = 推定の実稼働時間(h。小数第2位) */
export function estimateHours(windowH: number, coefficient: number): number {
  return Math.round(windowH * coefficient * 100) / 100;
}

/** bb_api_daily / bb_api_live の operators から、内部ID → 業務コード → 窓 を取り出す */
export function windowsOfDoc(
  doc: { operators?: Record<string, { by_code?: Record<string, { first_at?: string | null; last_at?: string | null; calls?: number; success?: number }> }> } | null | undefined,
): Map<string, Record<string, CallWindow>> {
  const out = new Map<string, Record<string, CallWindow>>();
  for (const [internalId, op] of Object.entries(doc?.operators ?? {})) {
    const codes: Record<string, CallWindow> = {};
    for (const [code, s] of Object.entries(op.by_code ?? {})) {
      if (!s.first_at || !s.last_at) continue;
      codes[code] = { first: s.first_at, last: s.last_at, talked: s.success ?? s.calls ?? 0 };
    }
    if (Object.keys(codes).length > 0) out.set(internalId, codes);
  }
  return out;
}

/** 内部ID の窓を BB ID に読み替える(1人が複数の内部IDを持つことがあるので業務コードごとに足す) */
export function windowsByBbId(byInternal: Map<string, Record<string, CallWindow>>, bbOfInternal: Map<string, string>): Map<string, Record<string, CallWindow>> {
  const out = new Map<string, Record<string, CallWindow>>();
  for (const [internalId, codes] of byInternal) {
    const bb = bbOfInternal.get(internalId);
    if (!bb) continue;
    const cur = out.get(bb) ?? {};
    for (const [code, w] of Object.entries(codes)) {
      const prev = cur[code];
      cur[code] = prev
        ? { first: prev.first < w.first ? prev.first : w.first, last: prev.last > w.last ? prev.last : w.last, talked: (prev.talked ?? 0) + (w.talked ?? 0) }
        : w;
    }
    out.set(bb, cur);
  }
  return out;
}
